import { createContext, useCallback, useContext, useLayoutEffect, useRef, useState, type ComponentProps, type Ref } from 'react';
import { Popover, PopoverSurface } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { useCompactScreen } from './session-state';
import { needsOverlaySheet } from './overlay-geometry';
import { useBrowserDismiss } from './browser-navigation';

const OverlayContext = createContext({ sheet: false, close: () => {}, register: (_element: HTMLDivElement | null) => {} });
export function useOverlaySheet() { return useContext(OverlayContext).sheet; }
export function AdaptivePopover(props: ComponentProps<typeof Popover>) {
  const compact = useCompactScreen();
  const [open, setOpen] = useState(props.defaultOpen ?? false);
  const [constrained, setConstrained] = useState(false);
  const [surface, register] = useState<HTMLDivElement | null>(null);
  const anchor = useRef<HTMLElement | null>(null);
  const expanded = props.open ?? open;
  useLayoutEffect(() => {
    if (!expanded) { setConstrained(false); anchor.current = null; return; }
    anchor.current ??= document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }, [expanded]);
  useLayoutEffect(() => {
    if (!expanded || compact || constrained || !surface) return;
    const measure = () => {
      const bottom = anchor.current?.getBoundingClientRect().bottom;
      if (bottom === undefined) return;
      // Measure content, not the already-clipped Floating UI wrapper. Shorter
      // screens and large pickers must become sheets before losing controls.
      const content = Math.max(surface.scrollHeight, surface.firstElementChild?.getBoundingClientRect().height ?? 0);
      if (needsOverlaySheet(window.innerHeight, bottom, content)) setConstrained(true);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(surface);
    if (surface.firstElementChild) observer.observe(surface.firstElementChild);
    window.addEventListener('resize', measure);
    return () => { observer.disconnect(); window.removeEventListener('resize', measure); };
  }, [expanded, compact, constrained, surface]);
  const sheet = compact || constrained;
  const close = () => { setOpen(false); props.onOpenChange?.(undefined as never, { open: false }); };
  useBrowserDismiss(expanded,close);
  return <OverlayContext.Provider value={{ sheet, close, register }}><Popover {...props} open={expanded} trapFocus={sheet} onOpenChange={(event, data) => {
    if (data.open && event.target instanceof HTMLElement) anchor.current = event.target.closest('button') ?? event.target;
    setOpen(data.open); props.onOpenChange?.(event, data);
  }}>{props.children}</Popover></OverlayContext.Provider>;
}
export function AdaptivePopoverSurface({ className = '', children, ref, ...props }: Omit<ComponentProps<typeof PopoverSurface>, 'ref'> & { ref?: Ref<HTMLDivElement> }) {
  const { sheet, close, register } = useContext(OverlayContext);
  const mergedRef = useCallback((element: HTMLDivElement | null) => {
    register(element);
    if (typeof ref === 'function') return ref(element);
    if (ref) ref.current = element;
  }, [ref, register]);
  const hasOwnHeader = className.includes('result-filter-host');
  return <PopoverSurface {...props} ref={mergedRef} className={`${className}${sheet ? ' adaptive-sheet' : ''}`}>
    {sheet && !hasOwnHeader && <header className="adaptive-sheet-heading"><strong>{props['aria-label'] ?? (className.includes('preset') ? '预设方案' : '选择')}</strong><button type="button" aria-label="关闭选择窗口" onClick={close}><DismissRegular /></button></header>}
    {children}
  </PopoverSurface>;
}
